import { ArrowRight, Info, SunHorizon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo, type CSSProperties } from 'react';
import { Link } from 'react-router';
import { ErrorState, Skeleton } from '@/components/primitives';
import { Footer } from '@/components/shell';
import { GridSkeleton, HeroStory, RankItem, RowStory, SideStory, StoryGrid, StoryTile } from '@/components/stories';
import { fetchDiscover, fetchTrending } from '@/lib/api/content';
import { useAuth } from '@/lib/auth';
import { greeting, longDate } from '@/lib/format';
import { useCategories, useDocumentTitle } from '@/lib/hooks';
import { useT, useUiLang } from '@/lib/i18n';
import { categoryColor, categoryLabel } from '@/lib/taxonomy';
import type { Story } from '@/lib/types';
import { useStoryKeys } from '@/lib/use-story-keys';

export default function Discover() {
  const t = useT();
  const lang = useUiLang();
  const { user, onboarded, contentLang, profile } = useAuth();
  const { data: cats } = useCategories();
  useDocumentTitle(null);

  const q = useQuery({ queryKey: ['discover', contentLang], queryFn: () => fetchDiscover(contentLang) });
  const trendingQ = useQuery({ queryKey: ['trending', contentLang], queryFn: () => fetchTrending(contentLang, 8) });

  const layout = useMemo(() => {
    const all = q.data?.stories ?? [];
    const withImg = all.filter((s) => s.cover_image_url);
    const hero = withImg[0] ?? all[0];
    const used = new Set<string>(hero ? [hero.id] : []);
    const side = all.filter((s) => !used.has(s.id) && s.dek).slice(0, 4);
    side.forEach((s) => used.add(s.id));
    const trending = (trendingQ.data ?? []).filter((s) => !used.has(s.id)).slice(0, 6);
    trending.forEach((s) => used.add(s.id));
    // Öne çıkan iki büyük kart görselli olmalı; geri kalanı tazelik sırasıyla.
    const pool = all.filter((s) => !used.has(s.id));
    const lead = pool.filter((s) => s.cover_image_url).slice(0, 2);
    const latest = [...lead, ...pool.filter((s) => !lead.includes(s))].slice(0, 14);
    latest.forEach((s) => used.add(s.id));
    const rest = all.filter((s) => !used.has(s.id));
    const byCat = new Map<string, Story[]>();
    for (const s of rest) {
      if (!s.category_id) continue;
      byCat.set(s.category_id, [...(byCat.get(s.category_id) ?? []), s]);
    }
    const blocks = [...byCat.entries()]
      .filter(([, list]) => list.length >= 3)
      .sort((a, b) => b[1].length - a[1].length)
      .slice(0, 4);
    const catCount = new Set(all.map((s) => s.category_id).filter(Boolean)).size;
    return { hero, side, trending, latest, blocks, total: all.length, catCount, leadCount: lead.length };
  }, [q.data, trendingQ.data]);

  const selected = useStoryKeys(layout.latest.map((s) => s.id));
  const firstName = profile?.full_name?.split(' ')[0];

  return (
    <>
      <section className="band">
        <div className="page">
          <div className="band-row">
            <div>
              <div className="band-eyebrow">
                {longDate()}
                {firstName ? ` · ${greeting()}, ${firstName}` : ''}
              </div>
              <h1 className="band-title">{t('discover_title')}</h1>
              {q.data ? (
                <p className="band-sub">{t('discover_sub', { n: layout.total, c: layout.catCount })}</p>
              ) : (
                <Skeleton h={18} w={320} style={{ marginTop: 14, opacity: 0.2 }} />
              )}
            </div>
            {user && onboarded && (
              <Link to="/today" className="btn btn-light">
                <SunHorizon size={18} /> {t('discover_open_today')}
              </Link>
            )}
          </div>
          <nav className="chips" aria-label={t('nav_categories')}>
            <Link to="/" className="chip active" aria-current="page">
              {t('all')}
            </Link>
            {(cats ?? []).map((c) => (
              <Link key={c.id} to={`/c/${c.slug}`} className="chip">
                <span className="dot" style={{ '--c': categoryColor(c.slug) } as CSSProperties} />
                {categoryLabel(lang, c.slug, c.name)}
              </Link>
            ))}
          </nav>
        </div>
      </section>

      <div className="page">
        {q.isError ? (
          <ErrorState onRetry={() => q.refetch()} />
        ) : !q.data ? (
          <div className="hero">
            <Skeleton h={440} r={24} />
            <div style={{ display: 'grid', gap: 14 }}>
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} h={96} r={16} />
              ))}
            </div>
          </div>
        ) : (
          <>
            {q.data.stale && (
              <div className="notice" style={{ marginBottom: 20 }}>
                <Info size={18} style={{ flexShrink: 0, marginTop: 1 }} />
                {t('discover_stale')}
              </div>
            )}
            <div className="hero">
              {layout.hero && <HeroStory story={layout.hero} />}
              <div className="hero-side">
                {layout.side.map((s) => (
                  <SideStory key={s.id} story={s} />
                ))}
              </div>
            </div>
          </>
        )}

        {layout.trending.length >= 3 && (
          <section className="section">
            <div className="section-head">
              <h2 className="section-title">{t('discover_trending')}</h2>
            </div>
            <div className="rank">
              {layout.trending.map((s) => (
                <RankItem key={s.id} story={s} />
              ))}
            </div>
          </section>
        )}

        {!(user && onboarded) && q.data && (
          <section className="section">
            <div className="cta-card">
              <div>
                <h3>{t('discover_personal_cta_title')}</h3>
                <p>{t('discover_personal_cta_body')}</p>
              </div>
              <Link to={user ? '/welcome' : '/signin?next=/welcome'} className="btn btn-light btn-lg">
                {t('discover_personal_cta_btn')} <ArrowRight size={18} />
              </Link>
            </div>
          </section>
        )}

        <section className="section">
          <div className="section-head">
            <h2 className="section-title">{t('discover_latest')}</h2>
          </div>
          {q.data ? <StoryGrid stories={layout.latest} selectedId={selected} featured={Math.min(2, layout.leadCount)} /> : <GridSkeleton />}
        </section>

        {layout.blocks.map(([catId, list]) => {
          const c = cats?.find((x) => x.id === catId);
          if (!c) return null;
          const color = categoryColor(c.slug);
          return (
            <section className="section" key={catId}>
              <div className="cat-block" style={{ '--c': color } as CSSProperties}>
                <div className="section-head">
                  <h2 className="section-title">
                    <span className="dot" style={{ '--c': color, width: 10, height: 10 } as CSSProperties} />
                    {t('discover_in_cat', { cat: categoryLabel(lang, c.slug, c.name) })}
                  </h2>
                  <Link to={`/c/${c.slug}`} className="section-link">
                    {t('discover_see_all')} <ArrowRight size={14} />
                  </Link>
                </div>
                <div className="cat-block-grid">
                  <StoryTile story={list[0]} />
                  <div className="row-list">
                    {list.slice(1, 4).map((s) => (
                      <RowStory key={s.id} story={s} />
                    ))}
                  </div>
                </div>
              </div>
            </section>
          );
        })}

        <Footer />
      </div>
    </>
  );
}
